import { useEffect, useState } from "react";
import { ExternalLink, X, AlertTriangle } from "lucide-react";

/**
 * Iframe-detection banner.
 *
 * Browsers block `getUserMedia` (and Web MIDI) inside embedded iframes by
 * default. This banner detects when ELPM Studio is running inside the
 * Emergent preview iframe and gives the user a one-click "Open in new tab"
 * action so recording / MIDI start working immediately.
 *
 * Hidden in production (outside iframe) so it doesn't add noise to the page.
 */
export default function IframeBanner() {
  const [inIframe, setInIframe] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setInIframe(window.self !== window.top);
    } catch (e) {
      // Cross-origin iframes throw on this check — which itself confirms we ARE iframed.
      setInIframe(true);
    }
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("elpm-iframe-banner-dismissed") : null;
    if (stored === "1") setDismissed(true);
  }, []);

  if (!inIframe || dismissed) return null;

  const openInNewTab = () => {
    window.open(window.location.href, "_blank", "noopener");
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("elpm-iframe-banner-dismissed", "1"); } catch (e) { void e; }
  };

  return (
    <div
      data-testid="iframe-banner"
      className="relative z-10 flex items-center gap-3 border-b border-amber-400/30 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-rose-500/10 px-4 py-2 backdrop-blur"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-300" />
      <p className="flex-1 font-mono text-[11px] leading-snug text-amber-100">
        <span className="font-bold uppercase tracking-wider">Mic + MIDI blocked in preview iframe.</span>{" "}
        Browsers don't allow microphone or Web MIDI access inside embedded previews.
        <span className="text-white/55"> Click → to open the studio in a real tab where Record actually works.</span>
      </p>
      <button
        type="button"
        data-testid="iframe-banner-open-btn"
        onClick={openInNewTab}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-amber-400 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-black shadow-[0_0_18px_rgba(251,191,36,0.5)] transition hover:bg-amber-300"
      >
        <ExternalLink className="h-3 w-3" />
        Open in new tab
      </button>
      <button
        type="button"
        data-testid="iframe-banner-dismiss"
        onClick={dismiss}
        title="Hide this banner for the session"
        className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md text-amber-200/65 hover:bg-white/[0.06] hover:text-amber-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
